import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { useSelector } from "react-redux";
import { isClientMissing, validateMemberObject } from "utils/userValidationUtil";
import { emailFieldProps, normalizeEmail } from "utils/emailValidationUtil";
import { addMemberToBackend, updateMemberToBackend } from "app/services/member.service";
import { loadUsersByRoleFromBackend } from "app/services/user.service";
import * as AppConstants from "AppConstants";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  clientEmail: "",
  userId: "",
  clientId: "",
};

const AddMemberDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const [memberUsers, setMemberUsers] = useState([]);
  const clients = useSelector((state) => state.client.rows);

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    if (!open) return;
    loadUsersByRoleFromBackend(AppConstants.ROLE_EMPLOYEE_MEMBER)
      .then((res) => setMemberUsers(res.data))
      .catch((error) => {
        setMemberUsers([]);
        handleAxiosError(error);
      });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editObject) {
      const clientId = editObject.client?.id;
      if (isClientMissing(clients, clientId)) {
        setData({
          id: parseInt(editObject.id),
          clientEmail: editObject.email,
          userId: parseInt(editObject.userId),
          clientId: "",
        });
        setErrorAction("The client for this member no longer exists. Please select a client.");
        return;
      }
      setData({
        id: parseInt(editObject.id),
        clientEmail: editObject.email,
        userId: parseInt(editObject.userId),
        clientId: parseInt(clientId),
      });
    } else {
      setData(obj);
    }
  }, [open, editObject, clients]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveMemberInBackend = async () => {
    const payload = trimTextFields({ ...data, clientEmail: normalizeEmail(data.clientEmail) });
    if (validateMemberObject(payload) && !editObject) await addMemberInvoker(payload);
    else if (validateMemberObject(payload) && editObject) await updateMemberInvoker(payload);
    else {
      setErrorAction("One or more required fields for a Member are missing or having invalid value");
    }
  };

  const addMemberInvoker = async (userObject) => {
    await addMemberToBackend(userObject)
      .then((data) => {
        setSuccessAction("Member added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add member. Please try again.");
      });
  };

  const updateMemberInvoker = async (data) => {
    const editObjectId = data.id;
    await updateMemberToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Member Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update member. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Member` : `Add New Member`}</DialogTitle>
      <DialogContent>
        {/* <DialogContentText></DialogContentText> */}
        <MDBox my={2}>
          <MasterDataSelect
            id="client"
            label="Client / Account"
            placeholder="Select a client"
            options={clients}
            value={data?.clientId || ""}
            onChange={(e) => setData({ ...data, clientId: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="user"
            label="InOpTra's Team Member"
            placeholder="Select a user"
            options={memberUsers}
            getOptionLabel={(user) => user.fullName}
            value={data?.userId || ""}
            onChange={(e) => setData({ ...data, userId: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="clientEmail"
            label="Email"
            fullWidth
            variant="outlined"
            value={data?.clientEmail || ""}
            onBlur={trimOnBlur(setData, "clientEmail")}
            onChange={(e) => setData({ ...data, clientEmail: e.target.value })}
            {...emailFieldProps(data?.clientEmail)}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveMemberInBackend} variant="contained">{editObject ? `Update` : `Add`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddMemberDialog);